import { useState, useEffect, useRef, useMemo } from "react";
import { Grid, CardContent, Typography, Paper, TextField, Button, Box } from "@mui/material";
import { IUser, IPolicyDoc, ICrewBase, IAPISParty, ISimpleUser, IPartialAPISParty } from "@vmosolutions/oc-interfaces";
import LoadingWrapper from "../../../LoadingWrapper/LoadingWrapper";
import PersonIcon from "@mui/icons-material/Person";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import { UsersForm } from "./UsersForm";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { useCancellablePromise } from "src/API/useCancellablePromise";
import { getPolicies } from "src/redux/actions/Auth/Auth.Actions";
import { IActiveModal } from "src/pages/Admin/AdminWindow";
import { getCrewBases } from "src/redux/actions/Structure.Actions";
import { DraggableListItem } from "src/components/DraggableListItem/DraggableListItem";
import AddIcon from "@mui/icons-material/Add";
interface IManageUsersProps {
  id: string;
  setActiveModal: (activeModal: IActiveModal | null) => void;
}

export const formatUserLabel = (user: IUser): string => {
  return `${user.lastName}, ${user.firstName} ${user?.employeeId ? `(${user.employeeId})` : ""}`;
};

export const sortUsersByLastName = (
  a: IUser | IAPISParty | ISimpleUser | IPartialAPISParty,
  b: IUser | IAPISParty | ISimpleUser | IPartialAPISParty
): any => {
  return a.lastName.toLowerCase().localeCompare(b.lastName.toLowerCase());
};

export const ManageUsers = (props: IManageUsersProps): JSX.Element => {
  const { setActiveModal } = props;
  const { cancellablePromise } = useCancellablePromise();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [users, setUsers] = useState<ISimpleUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [userFilter, setUserFilter] = useState<string>("");
  const [policies, setPolicies] = useState<IPolicyDoc[]>([]);
  const [crewBases, setCrewBases] = useState<ICrewBase[]>([]);

  useEffect(() => {
    const getUsersAction = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.GetPaginate<IUser>("/users?simpleUser=true", undefined, cancellablePromise);
      setIsLoading(false);
      if (Array.isArray(response)) setUsers(response.sort(sortUsersByLastName));
    };
    getUsersAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetrieveUser = (user: ISimpleUser): void => {
    if (user?._id) {
      const getUserInfoAction = async (): Promise<void> => {
        const userInfo = await AmplifyAPIWrapper.Get<IUser>(`/user/${user._id}`);
        const userDetails = userInfo?._id ? userInfo : user;
        setSelectedUser(userDetails as IUser);
      };
      getUserInfoAction();
    } else {
      setSelectedUser(null);
    }
  };

  const updateUserList = (op: IUser, mode: "create" | "update" | "delete"): void => {
    const newUsers = [...users];
    const index = users.findIndex((user: ISimpleUser) => user._id === op._id);
    switch (true) {
      case op._id && mode === "create":
        newUsers.push(op);
        setSelectedUser(op);
        break;
      case op._id && mode === "update" && index > -1:
        newUsers[index] = op;
        setSelectedUser(op);
        break;
      case op._id && mode === "delete" && index > -1:
        newUsers.splice(index, 1);
        setSelectedUser(null);
        break;
      default:
    }
    newUsers.sort(sortUsersByLastName);
    setUsers(newUsers);
  };

  useEffect(() => {
    const getPoliciesAction = async (): Promise<void> => {
      const response = await getPolicies();
      setIsLoading(false);
      if (Array.isArray(response)) {
        setPolicies(
          response.sort((a: IPolicyDoc, b: IPolicyDoc) => {
            return a.policyName.toLowerCase().localeCompare(b.policyName.toLowerCase());
          })
        );
      }
    };
    getPoliciesAction();
  }, []);

  useEffect(() => {
    const getCrewBasesAction = async (): Promise<void> => {
      const response: ICrewBase[] = await getCrewBases();
      if (Array.isArray(response)) setCrewBases(response);
    };
    getCrewBasesAction();
  }, []);

  return (
    <CardContent>
      {isLoading ? (
        <LoadingWrapper message="loading user data" />
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={3}>
            <Paper elevation={4} sx={{ pl: 1, pb: 1, width: "100%" }}>
              <Grid item xs={12}>
                <TextField
                  placeholder="Filter by name, email or employee ID"
                  variant="outlined"
                  size="small"
                  fullWidth
                  onChange={(e) => {
                    const val = e.target.value;
                    setUserFilter(val);
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  color="success"
                  variant="contained"
                  size="small"
                  onClick={() => setSelectedUser(null)}
                  sx={{ cursor: "pointer" }}
                >
                  <AddIcon fontSize="small" /> Add User
                </Button>
              </Grid>
              <Grid container item xs={12}>
                <Grid item sx={{ overflowY: "scroll", height: "67vh", width: "100%" }}>
                  {users
                    .filter((user) => {
                      const filter = userFilter.toLowerCase().replace(/[^a-zA-Z0-9\s]/g, "");
                      const sanitizedEmail = user.email.toLowerCase().replace(/[^a-zA-Z0-9\s]/g, "");
                      return (
                        user.firstName.toLowerCase().includes(filter) ||
                        user.lastName.toLowerCase().includes(filter) ||
                        sanitizedEmail.includes(filter) ||
                        (user?.employeeId && user?.employeeId.toLowerCase().includes(filter))
                      );
                    })
                    .sort((a, b) => {
                      if (a.active && !b.active) {
                        return -1;
                      } else if (!a.active && b.active) {
                        return 1;
                      } else {
                        if (a.lastName.toLowerCase() === b.lastName.toLowerCase()) {
                          return a.firstName.localeCompare(b.firstName);
                        } else {
                          return a.lastName.localeCompare(b.lastName);
                        }
                      }
                    })
                    .map((user, key) => {
                      return (
                        <DraggableListItem
                          key={key}
                          active={user._id === selectedUser?._id ? true : false}
                          onClick={() => handleRetrieveUser(user)}
                          sx={{ cursor: "pointer", display: "flex", alignitems: "center" }}
                        >
                          {user?.crewInfo ? (
                            <FolderSharedIcon fontSize="small" color={user.active ? "inherit" : "error"} />
                          ) : (
                            <PersonIcon fontSize="small" color={user.active ? "inherit" : "error"} />
                          )}
                          <Box sx={{ pl: 1 }}>
                            {user?.lastName}, {user?.firstName}
                          </Box>
                        </DraggableListItem>
                      );
                    })}
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={9}>
            <Typography variant="h6" className="form_title">
              Manage Users
              {selectedUser && (
                <>
                  {" "}
                  ({selectedUser.firstName} {selectedUser.lastName})
                </>
              )}
            </Typography>

            <UsersForm
              users={users}
              handleRetrieveUser={handleRetrieveUser}
              selectedUser={selectedUser}
              updateUserList={updateUserList}
              policies={policies}
              crewBases={crewBases}
              setActiveModal={setActiveModal}
            />
          </Grid>
        </Grid>
      )}
    </CardContent>
  );
};
